import { DRAFT_REQUEST_TIMEOUT_MS, isDraftPending } from '@/app/considerations/appliesToStatus';
import { useBusyAction } from '@/db/useBusyAction';
import { currentUserIdState } from '@/state/auth.state';
import { appliesToDraftRequestsState } from '@/state/hql.state';
import logger from '@/util/logger';
import { ConsiderationTypeDao, LessonTypeDao } from '@rock/db-admin';
import type { AppliesToOwner, ConsiderationType, UserId } from '@rock/types';
import { appliesToOwnerCopy, UserIdSchema } from '@rock/types';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useReducer } from 'react';
import { toast } from 'sonner';

const log = logger('useAppliesToActions');
const considerationTypeDao = new ConsiderationTypeDao();
const lessonTypeDao = new LessonTypeDao();

/**
 * The writes behind a consideration or lesson type's applies-to query. Approving is the only way a
 * query reaches `appliesTo`; callers pass a query that already validates (the editor won't enable
 * Approve otherwise).
 */
export function useAppliesToActions() {
    const { busy, run } = useBusyAction(log);
    const uid = useAtomValue(currentUserIdState);
    const setRequests = useSetAtom(appliesToDraftRequestsState);

    async function requestDraft(type: AppliesToOwner) {
        const wrote = await requestAppliesToDraft(type);
        setRequests((requests) => ({ ...requests, [type.id]: wrote }));
    }

    return {
        busy,
        approve: (type: AppliesToOwner, query: string, { clearDraft }: { clearDraft: boolean }) =>
            run('Approve query', async () => {
                if (!uid) throw new Error('Not signed in');
                const approvedBy = UserIdSchema.parse(uid);
                await approveAppliesTo(type, { query, approvedBy, clearDraft });
                toast.success('Query approved');
                log.info('Approved applies-to query', { typeId: type.id, kind: type.object, query, clearDraft });
            }),
        dismissDraft: (type: AppliesToOwner) =>
            run('Dismiss draft', async () => {
                await dismissAppliesToDraft(type);
                toast.success('Draft dismissed');
                log.info('Dismissed applies-to draft', { typeId: type.id, kind: type.object });
            }),
        redraft: (type: AppliesToOwner) =>
            run(`Process "${appliesToOwnerCopy(type).textLabel}" Again`, async () => {
                await requestDraft(type);
                toast.success(
                    `Processing "${appliesToOwnerCopy(type).textLabel}" again. A new draft appears here if it differs from the approved query.`,
                );
                log.info('Requested an applies-to redraft', { typeId: type.id, kind: type.object });
            }),
        /** The backfill: a draft for each type in `missing` (see `typesMissingQueries`). */
        draftMissing: (missing: ConsiderationType[]) =>
            run('Draft missing queries', async () => {
                for (const type of missing) await requestDraft(type);
                toast.success(
                    missing.length > 0 ? `Requested ${missing.length} drafts` : 'Every type has a query or a draft',
                );
                log.info('Requested drafts for types without a query', { count: missing.length });
            }),
    };
}

/**
 * Whether a type's requested draft is still on its way (see `isDraftPending`), so its buttons can wait
 * instead of asking twice. Re-renders when the oldest request times out.
 */
export function useIsDraftPending(): (type: AppliesToOwner) => boolean {
    const requests = useAtomValue(appliesToDraftRequestsState);
    const [, rerender] = useReducer((renders: number) => renders + 1, 0);

    useEffect(() => {
        const waits = Object.values(requests)
            .map((wrote) => (wrote ? Date.parse(wrote) : 0) + DRAFT_REQUEST_TIMEOUT_MS - Date.now())
            .filter((wait) => wait > 0);
        if (waits.length === 0) return;
        const timer = setTimeout(rerender, Math.min(...waits) + 100);
        return () => clearTimeout(timer);
    });

    return (type) => isDraftPending(type, requests[type.id], Date.now());
}

// The two catalogs share the applies-to writes but not the collection, so each write picks its DAO by kind.
function requestAppliesToDraft(type: AppliesToOwner) {
    return type.object === 'lesson-type'
        ? lessonTypeDao.requestAppliesToDraft(type)
        : considerationTypeDao.requestAppliesToDraft(type);
}

function approveAppliesTo(type: AppliesToOwner, input: { query: string; approvedBy: UserId; clearDraft: boolean }) {
    return type.object === 'lesson-type'
        ? lessonTypeDao.approveAppliesTo(type.id, input)
        : considerationTypeDao.approveAppliesTo(type.id, input);
}

function dismissAppliesToDraft(type: AppliesToOwner) {
    return type.object === 'lesson-type'
        ? lessonTypeDao.dismissAppliesToDraft(type.id)
        : considerationTypeDao.dismissAppliesToDraft(type.id);
}
